// hooks/useFollowComments.tsx
// フォローコメントの共有ロジック（追加・編集・削除・通知先選択）
//
// ホスト側の使い方:
//   const followComments = useFollowComments({ followno, contactUsers, initialComments });
//   followno が変われば内部状態は自動リセットされ initialComments を反映する（他の共有フックと同じアンカー方式）
//   一覧:   followComments.comments
//   追加:   followComments.openAdd()
//   編集:   followComments.openEdit(comment)
//   削除:   followComments.requestDelete(comment)
//   本人か: followComments.isOwnComment(comment)（サーバー側チェックはなくクライアント側のみ。PC版と同じ方針）
//   描画:   {followComments.renderModals()}
//
// 保存フロー（PC版 followComment.js 準拠）:
//   入力モーダルで保存 → 必ず通知先選択モーダル（NotifRecipientModal）を経由 →
//   確定時に saveFollowComment を呼ぶ（通知先0件でも保存自体は行われる）

import React, { useState, useEffect } from 'react';
import { message, Modal, Button } from 'antd';
import FollowCommentModal from '../components/FollowCommentModal';
import NotifRecipientModal from '../components/NotifRecipientModal';
import { followAPI, buildNoticeDestinationList } from '../services/follow-api';
import { useAuth } from '../contexts/AuthContext';
import { FollowComment } from '../types/follow';
import { MODAL_Z } from '../utils/modalZIndex';

export interface UseFollowCommentsOptions {
  /** 対象フォローNo（未選択時は null。null の間は追加/編集操作は無視される） */
  followno: number | null;
  /** 通知先候補ユーザー一覧 */
  contactUsers: { userno: number; username: string; deptname: string }[];
  /** followno に対応する初期コメント一覧（selectFollow 等から取得済みのものを渡す） */
  initialComments?: FollowComment[];
  /** 保存・削除でコメント一覧が変わったときの通知（ホスト側の別state同期用） */
  onCommentsChange?: (comments: FollowComment[]) => void;
  /** モーダルの基準 zIndex（入力・通知先選択モーダルに使用。削除確認はさらに+10） */
  zIndex?: number;
}

export interface UseFollowCommentsReturn {
  comments: FollowComment[];
  openAdd: () => void;
  openEdit: (comment: FollowComment) => void;
  requestDelete: (comment: FollowComment) => void;
  isOwnComment: (comment: FollowComment) => boolean;
  renderModals: () => React.ReactNode;
}

export const useFollowComments = (
  options: UseFollowCommentsOptions,
): UseFollowCommentsReturn => {
  const { followno, contactUsers, initialComments, onCommentsChange, zIndex = MODAL_Z.L2 } = options;
  const { user } = useAuth();

  const [comments, setComments] = useState<FollowComment[]>(initialComments ?? []);
  const [inputModalVisible, setInputModalVisible] = useState<boolean>(false);
  const [editingComment, setEditingComment] = useState<FollowComment | null>(null);
  const [pendingText, setPendingText] = useState<string>('');
  const [notifModalVisible, setNotifModalVisible] = useState<boolean>(false);
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<FollowComment | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // followno 切替: モーダルを閉じる（コメント一覧の同期は下の effect が別途担当）
  useEffect(() => {
    setInputModalVisible(false);
    setEditingComment(null);
    setPendingText('');
    setNotifModalVisible(false);
    setDeleteConfirmVisible(false);
    setDeleteTarget(null);
  }, [followno]);

  // コメント一覧を initialComments へ同期する。
  // followno 切替時だけでなく、同一 followno のまま initialComments が後から届く場合
  // （ホストが selectFollow 等を非同期取得し、followno 確定 → データ到着の順になるケース）にも追従する。
  useEffect(() => {
    setComments(initialComments ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialComments]);

  const isOwnComment = (comment: FollowComment): boolean =>
    user?.userno != null && comment.authorUserno === user.userno;

  const openAdd = () => {
    if (followno == null) return;
    setEditingComment(null);
    setInputModalVisible(true);
  };

  const openEdit = (comment: FollowComment) => {
    if (followno == null) return;
    setEditingComment(comment);
    setInputModalVisible(true);
  };

  const requestDelete = (comment: FollowComment) => {
    setDeleteTarget(comment);
    setDeleteConfirmVisible(true);
  };

  /** 入力モーダルの保存押下: テキストを保持し、通知先選択モーダルへ引き継ぐ */
  const handleInputSave = (text: string) => {
    setPendingText(text);
    setInputModalVisible(false);
    setNotifModalVisible(true);
  };

  /**
   * 通知先選択モーダルの確定押下: 実際の保存APIを呼ぶ。
   * 他の同種フロー（通知送信・フォロー紐づけ）と同じく、連打による二重送信を防ぐため
   * 必要な値を退避してからモーダルを即座に閉じ、非同期処理は退避した値で行う。
   */
  const handleNotifSubmit = async (recipients: { userno: number; urgent: boolean }[]) => {
    if (followno == null) return;
    const commentcontent = pendingText;
    const followcommentno = editingComment?.followcommentno;
    const isEdit = editingComment != null;
    setNotifModalVisible(false);
    setEditingComment(null);
    setPendingText('');
    try {
      const noticeDestinationList = buildNoticeDestinationList(recipients);
      const updated = await followAPI.saveFollowComment({
        followno,
        followcommentno,
        commentcontent,
        noticeDestinationList,
      });
      setComments(updated);
      onCommentsChange?.(updated);
      message.success(isEdit ? 'コメントを更新しました' : 'コメントを追加しました');
    } catch {
      message.error('コメントの保存に失敗しました');
    }
  };

  const confirmDelete = async () => {
    if (followno == null || deleteTarget == null) return;
    setDeleting(true);
    try {
      const updated = await followAPI.deleteComment(deleteTarget.followcommentno, followno);
      setComments(updated);
      onCommentsChange?.(updated);
      message.success('コメントを削除しました');
      setDeleteConfirmVisible(false);
      setDeleteTarget(null);
    } catch {
      message.error('コメントの削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  };

  const subZIndex = zIndex + 10;

  const renderModals = (): React.ReactNode => (
    <>
      {/* コメント入力モーダル（追加/編集共通） */}
      <FollowCommentModal
        open={inputModalVisible}
        mode={editingComment ? 'edit' : 'add'}
        initialText={editingComment?.text}
        onCancel={() => { setInputModalVisible(false); setEditingComment(null); }}
        onSave={handleInputSave}
        zIndex={zIndex}
      />

      {/* 通知先選択モーダル（保存の都度、必ず経由する） */}
      <NotifRecipientModal
        open={notifModalVisible}
        onCancel={() => setNotifModalVisible(false)}
        onSubmit={(recipients) => handleNotifSubmit(recipients)}
        contactUsers={contactUsers}
        title="通知先設定"
        submitLabel="保存"
        zIndex={zIndex}
      />

      {/* コメント削除確認モーダル */}
      {deleteConfirmVisible && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: subZIndex - 1 }} />
      )}
      <Modal
        title="コメントの削除"
        open={deleteConfirmVisible}
        onCancel={() => { if (!deleting) setDeleteConfirmVisible(false); }}
        centered
        closable={!deleting}
        maskClosable={!deleting}
        mask={false}
        zIndex={subZIndex}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button disabled={deleting} onClick={() => setDeleteConfirmVisible(false)}>キャンセル</Button>
            <Button danger type="primary" loading={deleting} onClick={confirmDelete}>削除</Button>
          </div>
        }
      >
        <p>このコメントを削除しますか？</p>
      </Modal>
    </>
  );

  return {
    comments,
    openAdd,
    openEdit,
    requestDelete,
    isOwnComment,
    renderModals,
  };
};
